import * as THREE from 'three';
import { FLOOR_PLAN_RENDER_QUALITY } from './floorPlanThreeRenderConfig.js';

const GRID_CENTER_COLOR = '#dbe4ec';
const GRID_LINE_COLOR = '#edf2f7';
const GRID_SIZE_METERS = 100;
const MAJOR_GRID_SPACING_METERS = 10;
const DEFAULT_LIGHTING = FLOOR_PLAN_RENDER_QUALITY.lighting;

/**
 * @param {HTMLDivElement} mount
 * @returns {THREE.WebGLRenderer}
 */
export function createFloorPlanWebGLRenderer(mount) {
  mount.innerHTML = '';

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    powerPreference: 'default',
    failIfMajorPerformanceCaveat: false,
  });
  mount.appendChild(renderer.domElement);
  return renderer;
}

/**
 * Release the WebGL context so React Strict Mode remounts can create a new renderer.
 * @param {THREE.WebGLRenderer | null | undefined} renderer
 * @param {HTMLDivElement | null | undefined} mount
 */
export function disposeFloorPlanWebGLRenderer(renderer, mount) {
  if (!renderer) return;

  const canvas = renderer.domElement;

  try {
    renderer.forceContextLoss?.();
  } catch {
    // Context may already be lost during teardown.
  }

  renderer.dispose();

  if (canvas?.parentNode) {
    canvas.parentNode.removeChild(canvas);
  }
  if (mount) {
    mount.innerHTML = '';
  }
}

/**
 * @param {THREE.WebGLRenderer} renderer
 */
export function configureFloorPlanRenderer(renderer) {
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.setClearColor(0x000000, 0);
  renderer.localClippingEnabled = true;

  if (process.env.NODE_ENV === 'development') {
    console.debug('[FloorPlan3D] renderer color settings', {
      outputColorSpace: renderer.outputColorSpace,
      toneMapping: renderer.toneMapping,
      toneMappingExposure: renderer.toneMappingExposure,
    });
  }
}

/**
 * @param {THREE.Scene} scene
 * @param {number} sceneRadius
 * @param {typeof DEFAULT_LIGHTING} [lightingConfig]
 * @returns {THREE.Light[]}
 */
export function createFloorPlanLights(scene, sceneRadius, lightingConfig = DEFAULT_LIGHTING) {
  const radius = Math.max(sceneRadius, 5);
  /** @type {THREE.Light[]} */
  const lights = [];

  const hemisphere = new THREE.HemisphereLight(
    lightingConfig.hemisphereSky,
    lightingConfig.hemisphereGround,
    lightingConfig.hemisphereIntensity,
  );
  scene.add(hemisphere);
  lights.push(hemisphere);

  const ambient = new THREE.AmbientLight(0xffffff, lightingConfig.ambientIntensity);
  scene.add(ambient);
  lights.push(ambient);

  const key = new THREE.DirectionalLight(0xffffff, lightingConfig.keyDirectionalIntensity);
  if (radius > 15) {
    key.position.set(radius * 0.55, radius * 1.2, radius * 0.45);
  } else {
    key.position.set(8, 12, 6);
  }
  scene.add(key);
  lights.push(key);

  const fill = new THREE.DirectionalLight(0xffffff, lightingConfig.fillDirectionalIntensity);
  fill.position.set(-radius * 0.6, radius * 0.8, -radius * 0.5);
  scene.add(fill);
  lights.push(fill);

  if (lightingConfig.upFillDirectionalIntensity > 0) {
    const upFill = new THREE.DirectionalLight(
      0xffffff,
      lightingConfig.upFillDirectionalIntensity,
    );
    upFill.position.set(0, -radius * 0.8, 0);
    scene.add(upFill);
    lights.push(upFill);
  }

  return lights;
}

/**
 * @param {THREE.Object3D} modelGroup
 * @param {{ minSize?: number; padding?: number }} [options]
 * @returns {{ footprint: number; centerX: number; centerZ: number }}
 */
export function computeGridFootprint(modelGroup, options = {}) {
  const { minSize = GRID_SIZE_METERS, padding = 1 } = options;
  const box = new THREE.Box3().setFromObject(modelGroup);
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();

  if (box.isEmpty()) {
    return { footprint: minSize, centerX: 0, centerZ: 0 };
  }

  box.getCenter(center);
  box.getSize(size);
  const modelAllowance = Math.max(size.x, size.z, 0) * 1.5;
  const requiredGridSize = Math.max(minSize, Math.ceil(modelAllowance + padding));
  const footprint = Math.ceil(requiredGridSize / 5) * 5;

  return {
    footprint,
    centerX: center.x,
    centerZ: center.z,
  };
}

/**
 * @param {number} footprint
 * @returns {THREE.GridHelper}
 */
export function createFloorPlanGrid(footprint) {
  const divisions = Math.max(2, Math.round(footprint / MAJOR_GRID_SPACING_METERS));
  const grid = new THREE.GridHelper(footprint, divisions, GRID_CENTER_COLOR, GRID_LINE_COLOR);
  grid.position.y = 0;
  return grid;
}

/**
 * @param {THREE.GridHelper} grid
 * @param {number} centerX
 * @param {number} centerZ
 */
export function positionFloorPlanGrid(grid, centerX, centerZ) {
  grid.position.set(centerX, 0, centerZ);
}

/**
 * @param {THREE.Light[]} lights
 */
export function disposeFloorPlanLights(lights) {
  lights.forEach((light) => {
    light.parent?.remove(light);
    light.dispose?.();
  });
}

/**
 * @param {THREE.GridHelper} grid
 */
export function disposeFloorPlanGrid(grid) {
  grid.geometry?.dispose();
  if (Array.isArray(grid.material)) {
    grid.material.forEach((material) => material.dispose());
  } else {
    grid.material?.dispose();
  }
  grid.parent?.remove(grid);
}
